/**
 * Settings → Styling saved per person (crm_users.styling): the theme and the
 * CRM's own settings (crmStyling.ts) as one object. The browser keeps using
 * localStorage — the embedded CRM and the chat list read it there — and the
 * profile is the record: on sign-in the saved object is written into
 * localStorage, and every change is sent back whole (debounced, so dragging
 * the scale slider is one save).
 */
import {
  CRM_FLAGS,
  CRM_STYLE_KEYS,
  CRM_STYLE_KEY_SET,
  CRM_TRANSLATE_TARGETS,
  FANSLY_MENU_STYLES,
  UNREAD_STYLES,
  readCrmStyling,
  writeCrmStyling,
  type CrmFlag,
  type CrmMediaView,
  type CrmTranslateTarget,
  type FanslyMenuStyle,
  type UnreadStyle,
} from '../crm/crmStyling';
import { useSyncExternalStore } from 'react';
import { parseTheme, readTheme, setTheme, THEME_STORAGE_KEY, type Theme } from './themes';
import { applyUiScale, scaleFactor } from './uiScale';

export interface UserStyling {
  theme: Theme;
  composerTranslate: CrmTranslateTarget;
  messageTranslate: CrmTranslateTarget;
  uiScale: number;
  uiScaleDefault: boolean;
  mediaView: CrmMediaView;
  fanslyMenu: FanslyMenuStyle;
  unreadStyle: UnreadStyle;
  flags: Record<CrmFlag, boolean>;
}

export type StylingPatch = Partial<Omit<UserStyling, 'flags'>> & { flags?: Partial<Record<CrmFlag, boolean>> };

/** What this browser uses now. */
export function currentStyling(): UserStyling {
  const { locale: _locale, ...crm } = readCrmStyling();
  return { theme: readTheme(), ...crm };
}

const oneOf = <T extends string>(list: readonly T[], v: unknown): T | undefined => ((list as readonly unknown[]).includes(v) ? (v as T) : undefined);

/** A saved object as a patch: anything unknown or malformed is left out. */
export function stylingPatchFrom(saved: unknown): StylingPatch {
  if (!saved || typeof saved !== 'object') return {};
  const s = saved as Record<string, unknown>;
  const patch: StylingPatch = {};
  const theme = parseTheme(s.theme);
  if (theme) patch.theme = theme;
  const composer = oneOf(CRM_TRANSLATE_TARGETS, s.composerTranslate);
  if (composer) patch.composerTranslate = composer;
  const message = oneOf(CRM_TRANSLATE_TARGETS, s.messageTranslate);
  if (message) patch.messageTranslate = message;
  if (typeof s.uiScale === 'number' && Number.isFinite(s.uiScale)) patch.uiScale = s.uiScale;
  if (typeof s.uiScaleDefault === 'boolean') patch.uiScaleDefault = s.uiScaleDefault;
  const media = oneOf(['expanded', 'compact'] as const, s.mediaView);
  if (media) patch.mediaView = media;
  const fanslyMenu = oneOf(FANSLY_MENU_STYLES, s.fanslyMenu);
  if (fanslyMenu) patch.fanslyMenu = fanslyMenu;
  const unreadStyle = oneOf(UNREAD_STYLES, s.unreadStyle);
  if (unreadStyle) patch.unreadStyle = unreadStyle;
  if (s.flags && typeof s.flags === 'object') {
    const flags: Partial<Record<CrmFlag, boolean>> = {};
    for (const flag of Object.keys(CRM_FLAGS) as CrmFlag[]) {
      const v = (s.flags as Record<string, unknown>)[flag];
      if (typeof v === 'boolean') flags[flag] = v;
    }
    if (Object.keys(flags).length) patch.flags = flags;
  }
  return patch;
}

/** Use a patch in this browser. Returns false when storage is unavailable (the theme still applies). */
export function applyStyling(patch: StylingPatch): boolean {
  const { theme, ...crm } = patch;
  let ok = true;
  if (theme && theme !== readTheme()) ok = setTheme(theme) && ok;
  if (Object.keys(crm).length) ok = writeCrmStyling(crm) && ok;
  stylingChanged();
  return ok;
}

/* ── Live styling: what the app reads, following every change ───── */

const listeners = new Set<() => void>();
let snapshot: UserStyling | null = null;

function stylingChanged() {
  snapshot = null;
  const s = currentStyling();
  applyUiScale(scaleFactor(s.uiScale, s.uiScaleDefault));
  listeners.forEach((fn) => fn());
}

export function onStylingChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function getSnapshot(): UserStyling {
  return (snapshot ??= currentStyling());
}

/** The styling in use, re-rendering when it changes here, in the CRM frame or in another tab. */
export function useStyling(): UserStyling {
  return useSyncExternalStore(onStylingChange, getSnapshot);
}

/** Apply the saved interface scale and follow styling changes made elsewhere. Called once at start-up. */
export function initStyling(): () => void {
  stylingChanged();
  const onStorage = (event: StorageEvent) => {
    if (isStylingKey(event.key)) stylingChanged();
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

/** Keys that carry styling, for following changes made in the CRM frame or another tab (not the language). */
export function isStylingKey(key: string | null): boolean {
  return key === null || key === THEME_STORAGE_KEY || (CRM_STYLE_KEY_SET.has(key) && key !== CRM_STYLE_KEYS.locale);
}

/**
 * Debounced sender of the whole current styling. Every call resolves (or
 * rejects) with the one save that covers it.
 */
export function createStylingUploader(send: (styling: UserStyling) => Promise<unknown>, delayMs = 400) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let waiting: { resolve: () => void; reject: (e: unknown) => void }[] = [];
  const flush = async () => {
    const batch = waiting;
    waiting = [];
    timer = undefined;
    try {
      await send(currentStyling());
      batch.forEach((w) => w.resolve());
    } catch (err) {
      batch.forEach((w) => w.reject(err));
    }
  };
  const schedule = () =>
    new Promise<void>((resolve, reject) => {
      waiting.push({ resolve, reject });
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(() => void flush(), delayMs);
    });
  const cancel = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
    waiting = [];
  };
  return { schedule, cancel };
}
